import { RouteEntity } from '../types';

type DriverActionKind = 'stop' | 'location';

export interface DriverQueuedAction {
  id: string;
  kind: DriverActionKind;
  method: 'patch' | 'post';
  url: string;
  body: object;
  createdAt: string;
}

const queueKey = 'otimizarota:driver-action-queue';
const routeKey = (routeId: string) => `otimizarota:driver-route:${routeId}`;

function readQueue(): DriverQueuedAction[] {
  try {
    const saved = localStorage.getItem(queueKey);
    const parsed = saved ? JSON.parse(saved) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveQueue(actions: DriverQueuedAction[]) {
  localStorage.setItem(queueKey, JSON.stringify(actions));
}

export function cacheDriverRoute(route: RouteEntity) {
  localStorage.setItem(routeKey(route.id), JSON.stringify({ savedAt: new Date().toISOString(), route }));
}

export function getCachedDriverRoute(routeId: string): RouteEntity | null {
  try {
    const saved = localStorage.getItem(routeKey(routeId));
    const parsed = saved ? JSON.parse(saved) : null;
    return parsed?.route?.id === routeId ? parsed.route as RouteEntity : null;
  } catch {
    return null;
  }
}

export function queueDriverAction(action: Omit<DriverQueuedAction, 'id' | 'createdAt'>) {
  const actions = readQueue();
  // Localização é volátil: guardar apenas a leitura mais recente evita uma fila
  // enorme quando o aparelho fica desconectado durante a rota.
  const withoutPreviousLocation = action.kind === 'location'
    ? actions.filter((item) => !(item.kind === 'location' && item.url === action.url))
    : actions;
  const queued: DriverQueuedAction = { ...action, id: crypto.randomUUID(), createdAt: new Date().toISOString() };
  saveQueue([...withoutPreviousLocation, queued]);
  return readQueue();
}

export function queuedDriverActionsCount() {
  return readQueue().length;
}

export async function flushDriverActions(send: (action: DriverQueuedAction) => Promise<void>) {
  const actions = readQueue();
  let sent = 0;

  for (let index = 0; index < actions.length; index += 1) {
    try {
      await send(actions[index]);
      sent += 1;
    } catch {
      const remaining = actions.slice(index);
      saveQueue(remaining);
      return { sent, remaining: remaining.length };
    }
  }

  saveQueue([]);
  return { sent, remaining: 0 };
}
